import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';

const IndividualProgressTracker = ({ auditData }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const participantData = [
    {
      id: 'U-001',
      name: 'John Anderson',
      email: 'john.anderson@company.com',
      department: 'Operations',
      status: 'completed',
      progress: 100,
      score: 92,
      completedQuestions: 45,
      totalQuestions: 45,
      timeSpent: '35 min',
      submittedDate: '2025-11-28',
      photoCount: 42
    },
    {
      id: 'U-002',
      name: 'Sarah Mitchell',
      email: 'sarah.mitchell@company.com',
      department: 'Safety',
      status: 'completed',
      progress: 100,
      score: 95,
      completedQuestions: 45,
      totalQuestions: 45,
      timeSpent: '42 min',
      submittedDate: '2025-11-25',
      photoCount: 45
    },
    {
      id: 'U-003',
      name: 'Mike Thompson',
      email: 'mike.thompson@company.com',
      department: 'Maintenance',
      status: 'in-progress',
      progress: 67,
      score: 88,
      completedQuestions: 30,
      totalQuestions: 45,
      timeSpent: '28 min',
      submittedDate: null,
      photoCount: 25
    },
    {
      id: 'U-004',
      name: 'Emily Rodriguez',
      email: 'emily.rodriguez@company.com',
      department: 'Operations',
      status: 'not-started',
      progress: 0,
      score: 0,
      completedQuestions: 0,
      totalQuestions: 45,
      timeSpent: '0 min',
      submittedDate: null,
      photoCount: 0
    },
    {
      id: 'U-005',
      name: 'David Chen',
      email: 'david.chen@company.com',
      department: 'Engineering',
      status: 'completed',
      progress: 100,
      score: 89,
      completedQuestions: 45,
      totalQuestions: 45,
      timeSpent: '38 min',
      submittedDate: '2025-12-01',
      photoCount: 40
    }
  ];

  const getStatusColor = (status) => {
    const colors = {
      'completed': 'bg-green-500/10 text-green-700 border-green-200',
      'in-progress': 'bg-orange-500/10 text-orange-700 border-orange-200',
      'not-started': 'bg-gray-500/10 text-gray-700 border-gray-200'
    };
    return colors?.[status] || colors?.['not-started'];
  };

  const getStatusIcon = (status) => {
    const icons = {
      'completed': 'CheckCircle',
      'in-progress': 'Clock',
      'not-started': 'Circle'
    };
    return icons?.[status] || 'Circle';
  };

  const getStatusLabel = (status) => {
    const labels = {
      'completed': 'Completed',
      'in-progress': 'In Progress',
      'not-started': 'Not Started'
    };
    return labels?.[status] || status;
  };

  const filteredParticipants = participantData?.filter(participant => {
    const matchesSearch = !searchQuery || 
      participant?.name?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
      participant?.email?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
      participant?.department?.toLowerCase()?.includes(searchQuery?.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || participant?.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  return (
    <div className="space-y-6">
      {/* Filters */}
      <div className="bg-card border border-border rounded-lg p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Search Participants
            </label>
            <Input
              type="text"
              placeholder="Search by name, email, or department..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e?.target?.value)}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Filter by Status
            </label>
            <div className="flex gap-2">
              <button
                onClick={() => setStatusFilter('all')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  statusFilter === 'all' ?'bg-primary text-primary-foreground' :'bg-muted text-foreground hover:bg-muted/80'
                }`}
              >
                All
              </button>
              <button
                onClick={() => setStatusFilter('completed')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  statusFilter === 'completed'
                    ? 'bg-primary text-primary-foreground' :'bg-muted text-foreground hover:bg-muted/80'
                }`}
              >
                Completed
              </button>
              <button
                onClick={() => setStatusFilter('in-progress')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  statusFilter === 'in-progress' ?'bg-primary text-primary-foreground' :'bg-muted text-foreground hover:bg-muted/80'
                }`}
              >
                In Progress
              </button>
              <button
                onClick={() => setStatusFilter('not-started')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  statusFilter === 'not-started' ?'bg-primary text-primary-foreground' :'bg-muted text-foreground hover:bg-muted/80'
                }`}
              >
                Not Started
              </button>
            </div>
          </div>
        </div>
      </div>
      {/* Participants List */}
      <div className="space-y-3">
        {filteredParticipants?.map((participant) => (
          <div
            key={participant?.id}
            className="bg-card border border-border rounded-lg p-6 hover:shadow-md transition-shadow"
          >
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <h3 className="text-base font-semibold text-foreground">{participant?.name}</h3>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(participant?.status)}`}>
                    <Icon name={getStatusIcon(participant?.status)} size={12} className="inline mr-1" />
                    {getStatusLabel(participant?.status)}
                  </span>
                </div>
                <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                  <div className="flex items-center gap-1">
                    <Icon name="Mail" size={14} />
                    <span>{participant?.email}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Icon name="Building" size={14} />
                    <span>{participant?.department}</span>
                  </div>
                  {participant?.submittedDate && (
                    <div className="flex items-center gap-1">
                      <Icon name="Calendar" size={14} />
                      <span>Submitted: {participant?.submittedDate}</span>
                    </div>
                  )}
                </div>
              </div>

              {participant?.status === 'completed' && (
                <div className="text-right">
                  <div className="text-2xl font-bold text-foreground mb-1">
                    {participant?.score}%
                  </div>
                  <div className="text-xs text-muted-foreground">Score</div>
                </div>
              )}
            </div>

            {/* Progress Bar */}
            <div className="mb-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-medium text-muted-foreground">
                  Progress: {participant?.completedQuestions}/{participant?.totalQuestions} questions
                </span>
                <span className="text-xs font-bold text-foreground">{participant?.progress}%</span>
              </div>
              <div className="w-full bg-muted rounded-full h-2">
                <div
                  className={`h-2 rounded-full transition-all ${
                    participant?.progress === 100 ? 'bg-green-500' :
                    participant?.progress > 0 ? 'bg-orange-500': 'bg-gray-300'
                  }`}
                  style={{ width: `${participant?.progress}%` }}
                />
              </div>
            </div>

            {/* Additional Metrics */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
              <div className="text-center p-2 bg-muted/30 rounded">
                <div className="text-xs text-muted-foreground mb-1">Time Spent</div>
                <div className="text-sm font-semibold text-foreground">{participant?.timeSpent}</div>
              </div>
              <div className="text-center p-2 bg-muted/30 rounded">
                <div className="text-xs text-muted-foreground mb-1">Photos</div>
                <div className="text-sm font-semibold text-foreground">{participant?.photoCount}</div>
              </div>
              {participant?.status === 'completed' && (
                <>
                  <div className="text-center p-2 bg-muted/30 rounded">
                    <div className="text-xs text-muted-foreground mb-1">Completion</div>
                    <div className="text-sm font-semibold text-green-600">✓ Done</div>
                  </div>
                  <div className="text-center p-2 bg-muted/30 rounded">
                    <div className="text-xs text-muted-foreground mb-1">Rating</div>
                    <div className="text-sm font-semibold text-foreground">
                      {participant?.score >= 90 ? 'Excellent' : 
                       participant?.score >= 80 ? 'Good' : 
                       participant?.score >= 70 ? 'Fair' : 'Needs Improvement'}
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex gap-2 pt-4 border-t border-border">
              {participant?.status === 'completed' && (
                <button className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors text-sm font-medium">
                  <Icon name="Eye" size={16} />
                  View Response
                </button>
              )}
              {participant?.status === 'in-progress' && (
                <button className="inline-flex items-center gap-2 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors text-sm font-medium">
                  <Icon name="Bell" size={16} />
                  Send Reminder
                </button>
              )}
              {participant?.status === 'not-started' && (
                <button className="inline-flex items-center gap-2 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors text-sm font-medium">
                  <Icon name="AlertCircle" size={16} />
                  Send Alert
                </button>
              )}
              <button className="inline-flex items-center gap-2 px-4 py-2 bg-muted text-foreground rounded-lg hover:bg-muted/80 transition-colors text-sm font-medium">
                <Icon name="MessageSquare" size={16} />
                Contact
              </button>
            </div>
          </div>
        ))}
      </div>
      {/* Empty State */}
      {filteredParticipants?.length === 0 && (
        <div className="bg-card border border-border rounded-lg p-12 text-center">
          <Icon name="Users" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-foreground mb-2">No participants found</h3>
          <p className="text-sm text-muted-foreground">
            Try adjusting your search or filter criteria
          </p>
        </div>
      )}
    </div>
  );
};

export default IndividualProgressTracker;